import { api } from "./api";
import { executarRequisicaoAutenticada } from "./authenticatedRequest";

const consultarAtivos = (rotaSistema: string, params?: Record<string, unknown>) =>
  executarRequisicaoAutenticada((token) => api.get(rotaSistema, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      }));

export interface OmexProTicker {
  symbol: string;
  base_symbol: string;
  quote_symbol: string;
  name?: string;
  image_url?: string;
  price?: number;
  volume_24h?: number;
  quote_volume_24h?: number;
  turnover_24h?: number;
  change_24h_pct?: number;
  high_24h?: number;
  low_24h?: number;
  trades_24h?: number;
  updated_at?: string;
  source?: string;
}

export interface OmexProTickerDetail {
  asset?: any;
  market?: any;
  order_book?: any;
  market_cap?: any;
  supply?: any;
  all_time?: any;
  returns?: any;
  network?: any;
  developer_activity?: any;
  community?: any;
  links?: any;
  depth?: any;
  derivatives?: any;
  coingecko?: any;
}

export interface OmexProMacroItem {
  id?: string;
  sourceId?: string;
  type?: string;
  source?: string;
  title?: string;
  summary?: string;
  author?: string | Record<string, unknown>;
  displayDate?: string;
  publishedAt?: string;
  sortDate?: string;
  assets?: string[];
  tags?: string[];
  fullNote?: string;
  images?: Array<string | Record<string, unknown>>;
  payload?: any;
}

export interface OmexProEtfInflowReport {
  id?: string;
  reportDate?: string;
  targetDate?: string;
  source?: string;
  assets?: any[];
  assetsByName?: Record<string, any>;
  errors?: any[];
  discord?: any;
  collectedAt?: string;
  publishedAt?: string;
}

const unwrapResponseData = <T,>(payload: any): T =>
  payload?.response?.data ?? payload?.data ?? payload;

const toArray = <T,>(value: any): T[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value && typeof value === "object") {
    return Object.values(value);
  }

  return [];
};

const unwrapTickerList = (payload: any): OmexProTicker[] => {
  const data = unwrapResponseData<any>(payload);

  return [
    ...toArray<OmexProTicker>(data?.items),
    ...toArray<OmexProTicker>(data?.tickers),
    ...toArray<OmexProTicker>(data?.assets),
    ...toArray<OmexProTicker>(data?.results),
    ...toArray<OmexProTicker>(Array.isArray(data) ? data : undefined),
  ].filter((item) => item?.symbol || item?.base_symbol);
};

export const obterTickersOmexPro = async (symbols: string[]) => {
  const uniqueSymbols = Array.from(new Set(symbols.map((symbol) => symbol.toUpperCase()).filter(Boolean)));

  if (!uniqueSymbols.length) {
    return [];
  }

  const response = await consultarAtivos("/Mercado/Tickers", {
      symbols: uniqueSymbols.join(","),
      quote_asset: "USDT",
      limit: uniqueSymbols.length,
      sort: "symbol",
  });

  return unwrapTickerList(response.data);
};

export const obterDetalheTickerOmexPro = async (symbol: string) => {
  const response = await consultarAtivos(
    `/Mercado/Tickers/${encodeURIComponent(symbol.toUpperCase())}`,
    {
      include_depth: true,
      include_derivatives: true,
      include_derivatives_series: false,
      depth_market: "futures",
      derivatives_period: "1h",
      derivatives_limit: 80,
    }
  );

  return unwrapResponseData<OmexProTickerDetail>(response.data);
};

export const obterMacroFeedOmexPro = async (limit = 8) => {
  const response = await consultarAtivos("/Mercado/Macro/Feed", { limit });
  const data = unwrapResponseData<any>(response.data);

  return [
    ...toArray<OmexProMacroItem>(data?.items),
    ...toArray<OmexProMacroItem>(data?.feed),
    ...toArray<OmexProMacroItem>(data?.results),
    ...toArray<OmexProMacroItem>(Array.isArray(data) ? data : undefined),
  ].filter((item) => item?.title || item?.summary);
};

export const obterEtfInflowsLatestOmexPro = async () => {
  const response = await consultarAtivos("/Mercado/Etfs/Inflows/Latest");

  return unwrapResponseData<OmexProEtfInflowReport>(response.data);
};
